import React, { useState, useRef, useCallback, useEffect } from 'react';

// YEREL SES KÜTÜPHANESİ
// Tüm yollar artık /sounds/ klasörüne işaret ediyor.
const SOUND_LIBRARY = {
  // Kesin olarak indirilip public/sounds klasörüne koyulduğunu varsaydığımız dosyalar:
  click: '/sounds/click.mp3',
  attack: '/sounds/attack.mp3',
  success: '/sounds/success.mp3',
  failure: '/sounds/failure.mp3',
  'dice-roll': '/sounds/dice-roll.mp3',
  expand: '/sounds/expand.mp3',
  'critical-hit': '/sounds/critical-hit.mp3',
  'game-over': '/sounds/game-over.mp3',
  'loading': '/sounds/loading.mp3',
  'gameplay': '/sounds/gameplay.mp3',
  'victory': '/sounds/victory.mp3',
  'defeat': '/sounds/failure.mp3',
  // Bulunması zor olanlar için geçici yerel dosyalar (siz bunları da indirmelisiniz):
  'game-start': '/sounds/success.mp3',
  'player-join': '/sounds/click.mp3',
  'level-up': '/sounds/critical-hit.mp3'
};

export const useSound = () => {
  const audioCache = useRef(new Map());
  const audioContext = useRef(null);
  // Merkezi state yönetimi
  const [isMuted, setIsMuted] = useState(false);

  // OPTİMİZASYON 1: isMuted state'inin güncel değerini tutmak için ref.
  // Bu, playSound useCallback'i içinde stale closure'ı engeller.
  const isMutedRef = useRef(isMuted);

  // Ref'i isMuted state'i her değiştiğinde güncelle.
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  const initAudioContext = useCallback(() => {
    if (typeof window !== 'undefined' && !audioContext.current) {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      audioContext.current = new AudioContext();

      const resumeAudio = () => {
        if (audioContext.current && audioContext.current.state === 'suspended') {
          audioContext.current.resume();
        }
        document.removeEventListener('click', resumeAudio);
        document.removeEventListener('touchstart', resumeAudio);
      };

      document.addEventListener('click', resumeAudio, { once: true });
      document.addEventListener('touchstart', resumeAudio, { once: true });
    }
  }, []);

  const playSound = useCallback((soundName, volume = 0.7) => {
    if (typeof window === 'undefined') return null;
    
    // DÜZELTME: isMuted state'i yerine güncel değeri tutan Ref'i kontrol et
    if (isMutedRef.current) {
      return null;
    }

    // AudioContext'i kontrol et ve gerekirse "uyandır"
    if (!audioContext.current || audioContext.current.state === 'suspended') {
      console.warn("AudioContext askıda, devam ettiriliyor...");
      initAudioContext();
      if (audioContext.current) {
        audioContext.current.resume();
      }
    }

    try {
      const cachedAudio = audioCache.current.get(soundName);
      let playPromise;

      if (cachedAudio) {
        // Önbellekteki sesleri kullan
        cachedAudio.volume = Math.max(0, Math.min(1, volume));
        cachedAudio.currentTime = 0;
        cachedAudio.muted = false;

        playPromise = cachedAudio.play();
      } else {
        // *** DÜZELTME: SADECE SOUND_LIBRARY KULLANILIYOR ***
        let soundUrl = SOUND_LIBRARY[soundName];
        if (!soundUrl) {
          console.warn(`Ses bulunamadı: ${soundName}`);
          return null;
        }

        const audio = new Audio(soundUrl);
        audio.volume = Math.max(0, Math.min(1, volume));
        audio.preload = 'auto';
        audio.muted = false;
        audioCache.current.set(soundName, audio);

        playPromise = audio.play();
      }

      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.warn(`Ses çalınamadı (${soundName}):`, error.name, error.message);
        });
      }

    } catch (error) {
      console.error('Ses çalma hatası:', error);
      return null;
    }
  }, [initAudioContext]); // DÜZELTME: isMuted dependency'si kaldırıldı

  const preloadSound = useCallback((soundName) => {
    if (typeof window === 'undefined') return;

    const soundUrl = SOUND_LIBRARY[soundName];
    if (!soundUrl) return;

    try {
      const audio = new Audio();
      audio.preload = 'auto';
      audio.src = soundUrl;

      // *** Yeni Eklenen Hata Dinleyicisi ***
      audio.onerror = (e) => {
        // Bu hata genellikle 404 veya dosya bozuk olduğunda ortaya çıkar.
        console.error(`🚨 HATA: Dosya yüklenemedi (preload) - ${soundName}. URL: ${soundUrl}. Hata Kodu:`, e.target.error.code);
      };
      // **********************************

      audio.load();

      audioCache.current.set(soundName, audio);
    } catch (error) {
      console.warn(`Ses ön yükleme hatası (${soundName}):`, error);
    }
  }, []);

  const preloadSounds = useCallback((soundList) => {
    soundList.forEach(soundName => {
      preloadSound(soundName);
    });
  }, [preloadSound]);


  const toggleMute = useCallback(() => {
    let newMuteState;
    setIsMuted(prevMuted => {
      newMuteState = !prevMuted;

      if (typeof window !== 'undefined') {
        localStorage.setItem('axiati_sound_muted', JSON.stringify(newMuteState));
      }
      return newMuteState;
    });

  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedMuteState = localStorage.getItem('axiati_sound_muted');
      if (savedMuteState !== null) {
        setIsMuted(JSON.parse(savedMuteState));
      }

      initAudioContext();
    }
  }, [initAudioContext]);

  return {
    playSound,
    preloadSounds,
    toggleMute,
    isMuted
  };
};